<template>
	<validation-provider
		tag="div"
		class="cassie-toggle-wrapper"
	>
		<v-switch
			:input-value="value"
			class="mt-0 cassie-hide-validation cassie-custom-toggle"
			:class="{ 'toggle-on': value, 'toggle-off': !value }"
			color="primary"
			:disabled="disabled"
			hide-details
			:data-testid="testId"
			inset
			dense
			@change="updateValue"
		>
			<template #label>
				<div
					class="d-flex align-center"
					:class="{ 'cassie-aligned-toggles': alignedToggles }"
				>
					<span
						v-if="shouldShowDynamicLabel"
						v-dompurify-html="currentLabel"
						class="cassie-toggle-dynamic-label"
						:class="labelColorClass"
						:style="currentLabelColor ? { color: currentLabelColor } : null"
					/>
					<v-tooltip
						v-if="tooltipText"
						slot="append"
						right
					>
						<template #activator="{ on }">
							<v-icon
								light
								class="ml-2"
								v-on="on"
							>
								mdi-information
							</v-icon>
						</template>
						<div v-dompurify-html="tooltipText" />
					</v-tooltip>
				</div>
			</template>
		</v-switch>
	</validation-provider>
</template>

<script>
import { VSwitch } from 'vuetify/lib'

export default {
	name: 'toggle',
	extends: VSwitch,
	props: {
		label: {
			type: String
		},
		tooltipText: {
			type: String
		},
		alignedToggles: {
			type: Boolean,
			default: false
		},
		value: {
			type: Boolean
		},
		disabled: {
			type: Boolean
		},
		toggleLabels: {
			type: Array,
			default: () => ['Disabled', 'Enabled']
		},
		retainLabelColour: {
			type: Boolean,
			default: false
		},
		showStatusLabel: {
			type: Boolean,
			default: false
		},
		toggleLabelColors: {
			type: Array,
			// [offColor, onColor]
			default: () => ['var(--global-cassie-switch-disabled-color)', 'var(--global-primary)']
		},
		testId: {
			type: String,
			default: 'cassie_toggle'
		}
	},
	computed: {
		currentLabel () {
			// If a static label prop is provided, use it; otherwise use toggleLabels array
			if (this.label) return this.label
			if (!this.shouldShowDynamicLabel) return ''
			return this.value ? this.toggleLabels[1] : this.toggleLabels[0]
		},
		currentLabelColor () {
			// Only apply dynamic color changes if showStatusLabel is true and retainLabelColour is false
			if (this.retainLabelColour || !this.showStatusLabel) return null
			return this.value ? this.toggleLabelColors[1] : this.toggleLabelColors[0]
		},
		labelColorClass () {
			// Only toggle classes (for legacy styling) when showStatusLabel is true
			if (!this.showStatusLabel) return null
			return this.value && !this.retainLabelColour ? 'cassie-switch-enabled' : 'cassie-switch-disabled'
		},
		shouldShowDynamicLabel () {
			// Show dynamic label if:
			// 1. A static label prop is given
			// 2. showStatusLabel is explicitly true (enables color + label)
			// 3. toggleLabels was explicitly passed by the parent (even if default values match)
			//    AND it has at least two entries (for off/on)
			if (this.label) return true
			if (this.showStatusLabel) return true
			const propsData = this.$options && this.$options.propsData
			const hasExplicitToggleLabels = propsData && Object.prototype.hasOwnProperty.call(propsData, 'toggleLabels')
			if (!hasExplicitToggleLabels) return false
			return Array.isArray(this.toggleLabels) && this.toggleLabels.length >= 2
		}
	},
	methods: {
		updateValue (value) {
			this.$emit('update:value', value)
		},
		statusToggleLabel (value) {
			if (this.label) return this.label
			if (!this.shouldShowDynamicLabel) return ''
			return value ? this.toggleLabels[1] : this.toggleLabels[0]
		}
	}
}
</script>

<style lang="scss">
	.cassie-switch-enabled {
		color: var(--global-syrenis-black);
	}

	.cassie-switch-disabled {
		color: var(--global-cassie-switch-disabled-color);
	}

	.cassie-hide-validation {
		.v-messages.v-messages {
			display: none !important;
		}

		/* Ensure the switch slot contents (thumb + track + label) are vertically centered */
		.v-input__slot {
			display: flex;
			align-items: center;
		}

		.v-input__prepend-outer {
			flex: 1 0 70%;
		}

		// Keep the toggle thumb (slider) white
		.v-input--switch__thumb {
			background-color: white !important;
			color: black !important;
			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;

			&::before {
				position: absolute;
				top: 52%;
				left: 48%;
				transform: translate(-50%, -50%);
				font-family: 'Material Design Icons';
				font-size: 12px;
				font-weight: normal;
				line-height: 1;
				z-index: 999;
				pointer-events: none;
			}
		}

		// Remove opacity on the track when toggled on
		.v-input--switch__track {
			opacity: 1 !important;
		}

		// Ensure the switch track maintains full opacity in all states
		.v-input--switch .v-input--selection-controls__input:not(.v-input--is-disabled) .v-input--switch__track {
			opacity: 1 !important;
		}
	}

	// Class-based approach for icons
	.cassie-custom-toggle {
		&.toggle-off .v-input--switch__thumb::before {
			content: '\F0156' !important; // mdi-close
			color: var(--global-foreground) !important;
			display: block !important;
		}

		&.toggle-on .v-input--switch__thumb::before {
			content: '\F012C' !important; // mdi-check
			color: var(--global-primary) !important;
			display: block !important;
		}
	}

	.cassie-aligned-toggles {
		width: 100%;
		justify-content: space-between;
	}

	/* Dynamic label styling */
	.cassie-toggle-dynamic-label {
		display: inline-flex;
		align-items: center;
		line-height: 1.1;
		margin-left: 8px;
	}

</style>
